import React from "react";
import "./kanban.css";

export interface KanbanColumn {
  value: string;
  label: string;
}
interface Props<T extends { id: number; status: string }> {
  columns: KanbanColumn[];
  cards: T[];
  renderCard: (card: T) => React.ReactNode;
  renderCollapsedCard?: (card: T) => React.ReactNode;
  onMove: (card: T, status: string) => void;
  disabled?: boolean;
  showMoveControl?: boolean;
  collapsed?: Record<string, boolean>;
  onToggle?: (status: string) => void;
  loading?: Record<string, boolean>;
  columnFeedback?: Record<string, React.ReactNode>;
}

// Domain-independent board: consumers own fetching, permissions and persistence.
export default function KanbanBoard<T extends { id: number; status: string }>({
  columns,
  cards,
  renderCard,
  renderCollapsedCard,
  onMove,
  disabled,
  showMoveControl = true,
  collapsed = {},
  onToggle,
  loading = {},
  columnFeedback = {},
}: Props<T>) {
  return (
    <div className="kanban-board" aria-label="Kanban board">
      {columns.map((column) => {
        const items = cards.filter((card) => card.status === column.value);
        const closed = collapsed[column.value];
        return (
          <section
            key={column.value}
            className={`kanban-column ${closed ? "is-collapsed" : ""}`}
            aria-label={column.label}
            onDragOver={(event) => {
              if (!disabled) event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (disabled) return;
              const card = cards.find(
                (item) =>
                  String(item.id) ===
                  event.dataTransfer.getData("application/x-kanban-card")
              );
              if (card && card.status !== column.value)
                onMove(card, column.value);
            }}
          >
            <header className="kanban-column-header">
              {onToggle ? (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  aria-expanded={!closed}
                  title={column.label}
                  onClick={() => onToggle?.(column.value)}
                >
                  <span aria-hidden="true">{closed ? "▸" : "▾"}</span>
                  <span className="kanban-column-title">{column.label}</span>
                </button>
              ) : (
                <strong className="kanban-column-title" title={column.label}>
                  {column.label}
                </strong>
              )}
              {!closed && (
                <span className="badge bg-secondary">{items.length}</span>
              )}
            </header>
            {closed && (
              <div className="kanban-collapsed-preview">
                {items.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    className="kanban-preview-card"
                    aria-label={`Expand ${column.label} to view card ${card.id}`}
                    onClick={() => onToggle?.(column.value)}
                  >
                    {renderCollapsedCard
                      ? renderCollapsedCard(card)
                      : `#${card.id}`}
                  </button>
                ))}
              </div>
            )}
            {!closed && (
              <div className="kanban-column-body">
                {columnFeedback[column.value]}
                {loading[column.value] ? (
                  <p role="status">Loading cards…</p>
                ) : (
                  <>
                    {items.map((card) => (
                      <article
                        key={card.id}
                        className="kanban-card"
                        draggable={!disabled}
                        onDragStart={(event) => {
                          event.dataTransfer.setData(
                            "application/x-kanban-card",
                            String(card.id)
                          );
                          event.dataTransfer.effectAllowed = "move";
                        }}
                      >
                        {renderCard(card)}
                        {showMoveControl && (
                          <label className="small d-block mt-3">
                            Move to
                            <select
                              className="form-select form-select-sm mt-1"
                              aria-label={`Move card ${card.id}`}
                              value={card.status}
                              disabled={disabled}
                              onChange={(event) =>
                                onMove(card, event.target.value)
                              }
                            >
                              {columns.map((target) => (
                                <option key={target.value} value={target.value}>
                                  {target.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </article>
                    ))}
                    {!items.length && !columnFeedback[column.value] && (
                      <p className="text-muted small">
                        No cards in this stage.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
